import {createColumnHelper} from "@tanstack/react-table";
import {truncateString} from "../../Utils/StringHelper";
import {TextInput} from "../../Components/Form/TextInput";
import {SecondaryButton} from "../../Components/Buttons/SecondaryButton";
import {BaseCard} from "../../Components/Cards/BaseCard";
import {TanStackTable} from "../../Components/Tables/TanStackTable";
import {useBoundStore} from "../../Store/store";
import {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";

export const MyInvoices = () => {
    const {t} = useTranslation();
    const invoices = useBoundStore((state) => state.invoices)
    const invoicesMetaData = useBoundStore((state) => state.invoicesMetaData)
    const getMyInvoices = useBoundStore((state) => state.getMyInvoices)
    const [statusFilter, setStatusFilter] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const handleFilterClick = (status) => {
        if(statusFilter.includes(status)) {
            setStatusFilter(statusFilter.filter(s => s !== status))
        } else {
            setStatusFilter([...statusFilter, status])
        }
        setCurrentPage(1);
    }

    useEffect(() => {
        getMyInvoices(statusFilter, '', currentPage)
    }, [statusFilter, currentPage]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage + 1);
    };

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('id', {
            header: t('tables.invoice_number'),
            cell: props => truncateString(props.cell.getValue()),
        }),
        columnHelper.accessor('performance_statement', {
            header: t('tables.invoice_period'),
            cell: props => {
                return dayjs.unix(props.cell.getValue()?.from?.timestamp).format('DD/MM/YYYY')
            },
        }),
        columnHelper.accessor('branch', {
            header: t('tables.customer'),
            cell: props => {
                return truncateString(`${props.cell.getValue()?.company_name}`)
            },
        }),
        columnHelper.accessor('amount', {
            header: t('tables.amount'),
            cell: props => `€ ${truncateString(props.cell.getValue())}`,
        }),
        columnHelper.accessor('document', {
            header: t('tables.document'),
            cell: props => {
                if(props.cell.getValue() !== null && props.cell.getValue()?.hasOwnProperty('url') ) {
                    return (
                        <div className={'flex flex-row space-x-4'}>
                            <FontAwesomeIcon icon={faDownload} className={'text-primary cursor-pointer'} onClick={() => window.open(props.cell.getValue().url, '_blank')}/>
                        </div>
                    )
                } else {
                    return (<p>-</p>)
                }
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.status'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <div>
                            {['pending','open'].includes(props.row.original.status) ? <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('general.status.open')}</div> : ''}
                            {props.row.original.status === 'paid' ? <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('general.status.paid')}</div> : ''}
                        </div>
                    </div>
                )
            },
        })
    ]

    const statusBadge = (status) => {
        const map = {
            pending: {cls: 'bg-orange-300 text-orange-900', label: t('general.status.open')},
            open: {cls: 'bg-orange-300 text-orange-900', label: t('general.status.open')},
            paid: {cls: 'bg-green-300 text-green-900', label: t('general.status.paid')},
        };
        const info = map[status];
        if (!info) return null;
        return <span className={`px-2 py-1 rounded-full text-xs ${info.cls}`}>{info.label}</span>;
    };

    return (
        <div className="px-4 md:px-0">
            <div className={'mb-2'}>
                <div className={'flex justify-between'}>
                    <div className={'my-auto w-full md:w-1/3'}>
                        <TextInput
                            type={'text'}
                            name={'search'}
                            translationIndex={null}
                            placeholder={t('general.actions.search')}
                            onChange={(e) => console.log(e.target.value)}
                        >
                        </TextInput>
                    </div>
                    <div className={'hidden md:grid grid-cols-2 gap-4 my-auto'}>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('pending')} onClick={() => {
                                handleFilterClick('pending')
                            }}>
                                {t('invoices.filter_unpaid')}
                            </SecondaryButton>
                        </div>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('paid')} onClick={() => {
                                handleFilterClick('paid')
                            }}>
                                {t('invoices.filter_paid')}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
                <div className={'grid md:hidden grid-cols-2 gap-2 mt-2'}>
                    <SecondaryButton outlined={!statusFilter.includes('pending')} onClick={() => handleFilterClick('pending')}>
                        {t('invoices.filter_unpaid')}
                    </SecondaryButton>
                    <SecondaryButton outlined={!statusFilter.includes('paid')} onClick={() => handleFilterClick('paid')}>
                        {t('invoices.filter_paid')}
                    </SecondaryButton>
                </div>
            </div>

            {/* Desktop: tabel */}
            <div className="hidden md:block">
                <BaseCard>
                    {invoices?.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">{t('invoices.no_results')}</p>
                    ) : (
                        <TanStackTable
                            columns={columns}
                            tableData={invoices}
                            laravelPaginationMetaData={invoicesMetaData}
                            page={currentPage - 1}
                            onChangePage={handlePageChange}
                        />
                    )}
                </BaseCard>
            </div>

            {/* Mobiel: cards */}
            <div className="md:hidden space-y-3">
                {invoices?.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">{t('invoices.no_results')}</p>
                )}
                {invoices?.map((invoice) => (
                    <BaseCard key={invoice.id}>
                        <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-gray-900 truncate">{invoice.branch?.company_name ?? '-'}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {invoice.performance_statement?.from?.timestamp
                                        ? dayjs.unix(invoice.performance_statement.from.timestamp).format('DD/MM/YYYY')
                                        : '-'}
                                </p>
                            </div>
                            {statusBadge(invoice.status)}
                        </div>
                        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                            <div className="text-xs text-gray-700 font-medium">
                                € {invoice.amount} &middot; #{invoice.id}
                            </div>
                            {invoice.document?.url && (
                                <button onClick={() => window.open(invoice.document.url, '_blank')} className="text-primary text-xs font-medium flex items-center gap-1">
                                    <FontAwesomeIcon icon={faDownload} className="text-xs"/>
                                    {t('tables.document')}
                                </button>
                            )}
                        </div>
                    </BaseCard>
                ))}
                {invoicesMetaData?.last_page > 1 && (
                    <div className="flex justify-center gap-2 py-2">
                        {Array.from({length: invoicesMetaData.last_page}, (_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`px-3 py-1 rounded text-sm ${currentPage === i + 1 ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'}`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}